/**
 * The hitch-free drain ladder, as pure functions.
 *
 * When Phase 3 reports a standing buffer the host climbs this ladder, one step per
 * DRAIN_VERIFY_MS, until the buffer clears, the excess halves, or the ladder is exhausted:
 *
 *   reassert -> pulse -> host-fps-dip (only when the depth is time-constant) -> exhausted
 *
 * Every lever is a receiver hint or a bounded host-side pacing change. Nothing here may
 * reconnect, renegotiate, restart ICE, replace a track, reload or reset a decoder: a lever
 * that hitches is not a drain, it is a different failure.
 *
 * The fps dip is gated on the H1 hypothesis: a time-based buffer drains when arrival slows
 * below playout; a frame-constant one (frames inside the decoder) only gets LONGER when the
 * frame rate drops, so it is never dipped. Browser-free and Node/Jest-safe.
 */

import type { StandingDepthHypothesis } from '../standingLatency/standingLatencyContracts';
import { DRAIN_MAX_ATTEMPTS } from './drainContracts';

export type DrainStep = 'reassert' | 'pulse' | 'host-fps-dip';

export type DrainOutcome =
  /** The standing verdict cleared inside the verify window. */
  | 'cleared'
  /** Excess fell by at least DRAIN_IMPROVED_FRACTION inside the verify window. */
  | 'improved'
  /** The step was applied and the excess did not move enough. */
  | 'no-effect'
  /** The step could not be applied (no receiver, closed channel, no lower fps rung). */
  | 'not-applied'
  /** The ladder has no further step for this episode. */
  | 'exhausted';

export type DrainLadderState = {
  /** Steps taken so far in this episode. */
  attempt: number;
  lastStep: DrainStep | null;
  startedAtMs: number;
  /** Excess when the episode began, ms. */
  baselineExcessMs: number;
};

/** A step succeeds when the excess falls to this fraction of its before-value or lower. */
export const DRAIN_IMPROVED_FRACTION = 0.5;

/** The host-side maxFramerate rungs the fps dip moves between, highest first. */
export const HOST_FPS_DIP_LADDER: ReadonlyArray<number> = [60, 45, 30];

const LADDER_ORDER: ReadonlyArray<DrainStep> = ['reassert', 'pulse', 'host-fps-dip'];

export function initialDrainLadderState(startedAtMs: number, baselineExcessMs: number | null): DrainLadderState {
  return { attempt: 0, lastStep: null, startedAtMs, baselineExcessMs: baselineExcessMs ?? 0 };
}

/**
 * The next step after `state.lastStep`, or null when the ladder is exhausted for this
 * hypothesis or `maxAttempts` steps have been taken.
 */
export function nextDrainStep(
  state: DrainLadderState,
  hypothesis: StandingDepthHypothesis,
  maxAttempts: number = DRAIN_MAX_ATTEMPTS,
): DrainStep | null {
  if (state.attempt >= maxAttempts) return null;
  const nextIndex = state.lastStep === null ? 0 : LADDER_ORDER.indexOf(state.lastStep) + 1;
  const candidate = LADDER_ORDER[nextIndex];
  if (candidate === undefined) return null;
  if (candidate === 'host-fps-dip' && hypothesis !== 'time-constant') return null;
  return candidate;
}

/** Record that `step` was taken. */
export function advanceDrainLadder(state: DrainLadderState, step: DrainStep): DrainLadderState {
  return { ...state, attempt: state.attempt + 1, lastStep: step };
}

/**
 * Judge one step against the excess it started from. `cleared` wins; otherwise the excess
 * must have fallen to DRAIN_IMPROVED_FRACTION of its before-value. An unmeasured after-value
 * is judged as no effect: the verdict, not the step, decides.
 */
export function judgeDrainStep(
  baselineExcessMs: number | null,
  afterExcessMs: number | null,
  cleared: boolean,
): DrainOutcome {
  if (cleared) return 'cleared';
  if (baselineExcessMs === null || afterExcessMs === null) return 'no-effect';
  if (baselineExcessMs <= 0) return 'no-effect';
  return afterExcessMs <= baselineExcessMs * DRAIN_IMPROVED_FRACTION ? 'improved' : 'no-effect';
}

/** True when the ladder need not continue: the buffer cleared or the step improved it enough. */
export function isDrainSuccess(outcome: DrainOutcome): boolean {
  return outcome === 'cleared' || outcome === 'improved';
}

/**
 * The highest HOST_FPS_DIP_LADDER rung strictly below `maxFramerate`, or null when there is
 * none (already at or below the bottom rung, or not a finite rate).
 */
export function nextLowerFpsRung(maxFramerate: number): number | null {
  if (!Number.isFinite(maxFramerate)) return null;
  return HOST_FPS_DIP_LADDER.find((rung) => rung < maxFramerate) ?? null;
}
